<template>
	<q-page padding>
		<div class="q-display-1 q-mb-md text-secondary">
			<span>
				<q-icon name="fas fa-list" class="q-mb-sm" /> {{ $t('library_categories') }}

				<q-tooltip anchor="bottom right" self="top left">
					{{ $t('category_help') }}
				</q-tooltip>
			</span>
		</div>

		<q-table
			:data="categories"
			:columns="columns"
			:pagination.sync="server_pagination"
			@request="request"
			:loading="loading"
			:filter="filter"
			row-key="name"
			:no-data-label="$t('no_data_avaliable')"
			:no-results-label="$t('no_results_found')"
			:loading-label="$t('loading_data')"
			:rows-per-page-label="$t('items_per_page')"
			:rows-per-page-options="[5, 10, 50, 100]"
			binary-state-sort
		>
			<template slot="top" slot-scope="props">
				<q-search
					v-model="filter"
					icon="fas fa-search"
					:placeholder="$t('search')"
					:debounce="500"
				/>
				<div class="col" />
				<q-btn
					color="secondary"
					icon="fas fa-plus"
					:label="$t('new_f')"
					@click="$router.push({ name: 'admin_library_category', params: { id: 0 } })"
				/>
			</template>

			<q-tr slot="header" slot-scope="props" :props="props">
				<q-th v-for="col in props.cols" :key="col.name" :props="props">
					{{ $t(col.label) }}
				</q-th>
			</q-tr>

			<q-tr slot="body" slot-scope="props" :props="props">
				<q-td key="id" :props="props">
					{{ props.row.id }}
				</q-td>

				<q-td key="title" :props="props">
					{{ props.row.title }}
				</q-td>

				<q-td key="qty_total_files" :props="props">
					{{ props.row.qty_total_files }}
				</q-td>

				<q-td key="is_featured" :props="props">
					{{ $t(getFeatured(props.row.is_featured)) }}
				</q-td>

				<q-td key="status" :props="props" auto-width>
					<q-chip
						small
						:icon="getIconStatus(props.row.status)"
						:color="getColorStatus(props.row.status)"
						class="full-width"
					>
						{{ $t(getStatus(props.row.status)) }}
					</q-chip>
				</q-td>

				<q-td key="icon" :props="props" class="text-faded">
					<q-icon :name="props.row.icon" size="1.5rem" />
				</q-td>

				<q-td key="actions" :props="props">
					<q-btn
						flat
						round
						color="primary"
						icon="fas fa-pencil-alt"
						@click="
							$router.push({
								name: 'admin_library_category',
								params: { id: props.row.actions },
							})
						"
					>
						<q-tooltip>
							{{ $t('view_or_edit_tooltip') }}
						</q-tooltip>
					</q-btn>

					<q-btn
						flat
						round
						color="negative"
						icon="far fa-trash-alt"
						@click="confirmRemove(props.row.actions)"
					>
						<q-tooltip>
							{{ $t('delete_tooltip') }}
						</q-tooltip>
					</q-btn>
				</q-td>
			</q-tr>
		</q-table>

		<q-dialog v-model="confirm_remove" @ok="removeItem">
			<span slot="title">{{ $t('remove_item') }}</span>

			<span slot="message">{{ $t('confirm_remove_item') }}</span>

			<template slot="buttons" slot-scope="props">
				<q-btn color="primary" :label="$t('cancel')" @click="confirm_remove = false" />
				<q-btn color="negative" :label="$t('remove')" @click="removeItem()" />
			</template>
		</q-dialog>
	</q-page>
</template>

<script>
import { mapGetters } from 'vuex'
import { LocalStorage } from 'quasar'
export default {
	i18nOptions: { namespaces: ['custom', 'common', 'library', 'admin'] },

	data() {
		return {
			server_pagination: {
				page: 1,
				rowsNumber: 10,
				rowsPerPage: null,
				descending: false,
				sortBy: null,
			},
			loading: false,
			categories: [],
			columns: [
				{
					name: 'id',
					required: true,
					label: '#',
					align: 'left',
					field: 'name',
					sortable: true,
				},
				{
					name: 'title',
					required: true,
					label: 'category',
					align: 'left',
					field: 'title',
					sortable: true,
				},
				{
					name: 'qty_total_files',
					required: true,
					label: 'qtd_items',
					align: 'left',
					field: 'qty_total_files',
					sortable: true,
				},
				{
					name: 'is_featured',
					required: true,
					label: 'featured',
					align: 'left',
					field: 'is_featured',
					sortable: true,
				},
				{
					name: 'status',
					required: true,
					label: 'status',
					align: 'center',
					field: 'status',
					sortable: false,
				},
				{
					name: 'icon',
					required: true,
					label: 'icon',
					align: 'center',
					field: 'icon',
					sortable: false,
				},
				{
					name: 'actions',
					required: true,
					label: '',
					align: 'right',
					field: 'edit',
					sortable: false,
				},
			],
			filter: '',
			action_id: '',
			confirm_remove: false,
		}
	},

	computed: {
		...mapGetters({
			access_token: 'common/access_token',
			id_user_client_country_selected: 'common/id_user_client_country_selected',
			reload_content: 'common/reload_content',
			num_rows: 'datatable/num_rows',
		}),

		getHeader() {
			let headers = {}
			return (headers = {
				headers: {
					'content-type': 'application/json',
					authorization:
						'Basic ' +
						btoa(process.env.APP_CLIENT_ID + ':' + process.env.APP_CLIENT_SECRET),
					authorization: 'Bearer ' + this.access_token,
				},
			})
		},
	},

	methods: {
		request({ pagination }) {
			this.loading = true
			this.server_pagination = pagination

			let order = pagination.descending ? '-' : ''
			let order_by = pagination.sortBy ? order + pagination.sortBy : ''

			let data = {
				page_size: this.server_pagination.rowsPerPage,
				page: this.server_pagination.page,
				search: this.filter,
				ordering: order_by,
			}

			this.savePagination()

			this.$axios
				.get('/admin_library_files/categories/?' + this.$qs.stringify(data), this.getHeader)
				.then((response) => {
					this.server_pagination.rowsNumber = response.data.count

					this.categories = []
					response.data.results.forEach((result) => {
						this.categories.push({
							id: result.id,
							is_featured: result.is_featured,
							icon: result.icon,
							qty_total_files: result.qty_files,
							status: result.status,
							title: result.title,
							actions: result.id,
						})
					})

					this.loading = false
				})
				.catch((e) => {
					this.loading = false
					console.log(e)
				})
		},

		fetchLists() {
			this.$q.loading.show()

			this.$axios
				.get('/accounts/get_registration_lists/', this.getHeader)
				.then((response) => {
					this.$store.dispatch('registration_lists/SET_LISTS', [response.data])
					this.$q.loading.hide()

					this.request({
						pagination: this.server_pagination,
					})
				})
				.catch((e) => {
					this.$q.loading.hide()
					console.log(e)
				})
		},

		confirmRemove(id) {
			this.action_id = id
			this.confirm_remove = true
		},

		removeItem() {
			this.$q.loading.show()

			this.$axios
				.delete(`/admin_library_files/categories/${this.action_id}/`, this.getHeader)
				.then((response) => {
					this.$q.loading.hide()

					this.request({
						pagination: this.server_pagination,
					})

					this.confirm_remove = false

					this.$q.notify({
						message: this.$t('item_removed_success'),
						type: 'positive',
						icon: 'far fa-trash-alt',
					})
				})
				.catch((e) => {
					this.$q.loading.hide()
					console.log(e)
				})
		},

		getFeatured(featured) {
			if (featured == 1) return 'yes'
			else return 'no'
		},

		getStatus(status) {
			if (status == 1) return 'active_f'
			else if (status == 2) return 'inactive_f'
			else if (status == 3) return 'deleted_f'
		},

		getIconStatus(status) {
			if (status == 1) return 'fas fa-check'
			else if (status == 2) return 'fas fa-ban'
			else if (status == 3) return 'fas fa-trash-alt'
		},

		getColorStatus(status) {
			if (status == 1) return 'positive'
			else if (status == 2) return 'warning'
			else if (status == 3) return 'negative'
		},

		savePagination() {
			this.$store.dispatch('datatable/SET_NUM_ROWS', [this.server_pagination.rowsPerPage])
		},

		loadPagination() {
			this.server_pagination.rowsPerPage = this.num_rows
		},
	},

	watch: {
		reload_content: function() {
			this.request({
				pagination: this.server_pagination,
			})
		},
	},

	created() {
		this.loadPagination()
		this.fetchLists()
	},
}
</script>

<style lang="stylus" scoped></style>
